import { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Select, Spin, Table, Tag } from "antd";
import type { TableColumnsType } from "antd";
import { AdminListShell } from "../components/admin/AdminListShell";
import { fetchAdminFeedbackPage } from "../api/client";
import type { AdminFeedbackRow } from "../api/types";
import { fmtDateTime } from "../lib/formatAdmin";

const STATUS_OPTS = [
  { value: "", label: "全部" },
  { value: "open", label: "open" },
  { value: "closed", label: "closed" },
];

export function FeedbackPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState("");
  const [items, setItems] = useState<AdminFeedbackRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<"forbidden" | "error" | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [active, setActive] = useState<AdminFeedbackRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const r = await fetchAdminFeedbackPage({
      page,
      page_size: pageSize,
      status: status || undefined,
    });
    if (r === "forbidden") setErr("forbidden");
    else if (r === "error") setErr("error");
    else {
      setItems(r.items);
      setTotal(r.total);
    }
    setLoading(false);
  }, [page, pageSize, status]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [status]);

  const columns: TableColumnsType<AdminFeedbackRow> = [
    { title: "联系方式", dataIndex: "contact", key: "c", width: 160, ellipsis: true },
    {
      title: "摘要",
      dataIndex: "question",
      key: "q",
      ellipsis: true,
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 88,
      render: (s: string) => <Tag color={s === "open" ? "orange" : "default"}>{s}</Tag>,
    },
    {
      title: "用户ID",
      dataIndex: "user_id",
      width: 120,
      render: (id: string) => <span className="admin-ledger-mono">{id.slice(0, 8)}…</span>,
    },
    { title: "创建", dataIndex: "created_at", width: 168, render: fmtDateTime },
    {
      title: "操作",
      key: "op",
      width: 80,
      fixed: "right",
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => { setActive(row); setDrawerOpen(true); }}>
          全文
        </Button>
      ),
    },
  ];

  return (
    <>
      <AdminListShell
        title="反馈工单"
        description="用户提交的咨询与问题（正文可能被截断，点全文查看）。"
        err={err}
        errorBanner
        toolbar={
          <div className="admin-ledger-toolbar">
            <Select
              value={status || ""}
              onChange={(v) => setStatus(v)}
              options={STATUS_OPTS}
              style={{ minWidth: 120 }}
              variant="borderless"
            />
          </div>
        }
        table={(bodyY) => (
        <div className="admin-ledger-table-wrap admin-ledger-table-wrap--flex">
          <Spin spinning={loading}>
            <Table<AdminFeedbackRow>
              rowKey="id"
              className="admin-ledger-table"
              columns={columns}
              dataSource={items}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50],
                showTotal: (t) => `共 ${t} 条`,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
              }}
              scroll={{ x: 1024, y: bodyY }}
              locale={{ emptyText: "暂无工单" }}
            />
          </Spin>
        </div>
      )}
    />

      <Drawer
        title="工单正文"
        placement="right"
        size="40%"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setActive(null); }}
        destroyOnHidden
        className="admin-drawer"
      >
        {active ? (
          <>
            <p className="admin-ledger-mono" style={{ marginBottom: 16 }}>
              {active.contact} · {fmtDateTime(active.created_at)}
            </p>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6 }}>{active.question}</div>
          </>
        ) : null}
      </Drawer>
    </>
  );
}
